import { UiButtonDirective } from '@template-ui/directives';
import { UiTabGroupComponent } from '@template-ui/components';
import { WorkflowEntryPointsComponent } from './workflow-entry-points.component';
import { HearingEligibilityTestComponent } from './hearing-eligibility-test.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  computed,
  signal,
  viewChild,
  inject,
} from '@angular/core';

import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';
import { workflowDraftActionLabel } from './platform-workflow-definition-lifecycle.models';
import type { WorkflowDefinitionVersionDetail } from './platform-workflow-registry-diagnostics.models';
import { PlatformWorkflowDraftDecisionEditorComponent } from './platform-workflow-draft-decision-editor.component';
import { PlatformWorkflowDraftEdgeEditorComponent } from './platform-workflow-draft-edge-editor.component';
import {
  PlatformWorkflowDraftTransitionEditorComponent,
  type WorkflowDraftTransitionDecisionRulesRequest,
} from './platform-workflow-draft-transition-editor.component';
import { projectWorkflowDraftGraph } from './workflow-draft-graph-projection';
import { WorkflowReadOnlyGraphComponent } from './workflow-read-only-graph.component';
import { WorkflowAssignmentPolicyComponent } from './workflow-assignment-policy.component';
import { WorkflowDefinitionLifecycleActionsComponent } from './workflow-definition-lifecycle-actions.component';
import { workflowVersionReviewPresentation } from './workflow-version-presentation';
import { readWorkflowPublishedInspection } from './workflow-published-inspection';
import {
  readWorkflowPublishedWorkInspection,
  type WorkflowPublishedWorkInspection,
  type WorkflowPublishedWorkSection,
  type WorkflowPublishedWorkSectionId,
} from './workflow-published-work-inspection';
import {
  readWorkflowPublishedStepPolicyInspection,
  type WorkflowPublishedStepPolicySectionId,
} from './workflow-published-step-policy-inspection';
import { readWorkflowPublishedProgressInspection } from './workflow-published-progress-inspection';
import {
  readWorkflowPublishedWorkflowSettingsInspection,
  type WorkflowPublishedWorkflowSettingsSectionId,
} from './workflow-published-workflow-settings-inspection';
import {
  readWorkflowPublishedRoutingInspection,
  type WorkflowPublishedRoutingTarget,
} from './workflow-published-routing-inspection';
import {
  readWorkflowPublishedStatusInspection,
  type WorkflowPublishedStatusTarget,
  type WorkflowPublishedStatusSectionId,
} from './workflow-published-status-inspection';
import {
  readWorkflowPublishedPresentationInspection,
  type WorkflowPublishedPresentationInspection,
  type WorkflowPublishedPresentationSectionId,
} from './workflow-published-presentation-inspection';
import type {
  WorkflowReadOnlyGraphDecisionGate,
  WorkflowReadOnlyGraphDecisionGateRequest,
  WorkflowReadOnlyGraphDecisionScopeRequest,
  WorkflowReadOnlyGraphEdge,
  WorkflowReadOnlyGraphElementDetail,
  WorkflowReadOnlyGraphNode,
  WorkflowReadOnlyGraphSelection,
  WorkflowReadOnlyGraphSelectionRequest,
} from './workflow-read-only-graph.models';

import { WorkspaceHostPreviewComponent } from './workspace-host-preview.component';

import { WorkflowExecutionPanelComponent } from './workflow-execution-panel.component';

export type WorkflowVersionWorkbenchView = 'map' | 'setup' | 'review';
export type WorkflowPublishedDraftAction = 'create' | 'continue' | 'ambiguous';
export interface WorkflowPublishedDraftRequest {
  readonly definitionVersionId: number;
  readonly workflowCode: string;
  readonly definitionVersionCode: string;
  readonly sourceContext: string;
  readonly actionCode: WorkflowPublishedDraftAction;
  readonly actionContext: string;
  readonly view: WorkflowVersionWorkbenchView;
  readonly ownerContext: string;
}
interface WorkflowPublishedDraftEntry {
  readonly source: WorkflowDefinitionVersionDetail;
  readonly request: WorkflowPublishedDraftRequest;
}
export function workflowPublishedDraftAction(
  value: unknown,
): WorkflowPublishedDraftAction | null {
  return value === 'create' || value === 'continue' || value === 'ambiguous'
    ? value
    : null;
}
/** Publication identity used by catalog handoffs and live source comparisons. */
export function workflowPublishedDraftSourceContext(
  source: Pick<
    WorkflowDefinitionVersionDetail,
    | 'id'
    | 'workflowCode'
    | 'definitionVersionCode'
    | 'canonicalContentHash'
    | 'configurationVersionId'
  >,
): string {
  return JSON.stringify([
    source.id,
    source.workflowCode,
    source.definitionVersionCode,
    source.canonicalContentHash,
    source.configurationVersionId,
  ]);
}

type WorkflowPublishedSettingsTask =
  | 'general'
  | 'data'
  | 'assignment'
  | 'technical'
  | WorkflowPublishedWorkflowSettingsSectionId;
type WorkflowPublishedWorkTask =
  | Exclude<WorkflowPublishedWorkSectionId, 'otherCommands'>
  | WorkflowPublishedStepPolicySectionId
  | 'progress';
interface WorkflowPublishedWorkEntry {
  context: string;
  selection: string;
  target: WorkflowPublishedWorkInspection['target'];
}
type WorkflowPublishedRoutingTask = 'paths' | 'results';
interface WorkflowPublishedRoutingEntry {
  context: string;
  selection: string;
  target: WorkflowPublishedRoutingTarget;
}
type WorkflowPublishedStatusView = 'map' | 'setup';
interface WorkflowPublishedStatusEntry {
  context: string;
  selection: string;
  view: WorkflowPublishedStatusView;
  target: WorkflowPublishedStatusTarget;
}
interface WorkflowPublishedTechnicalDocumentEntry {
  context: string;
  viewGeneration: number;
}
interface WorkflowPublishedPresentationEntry {
  context: string;
  selection: string;
  target: WorkflowPublishedPresentationInspection['target'];
}

type WorkflowVersionInspectorPane =
  | 'details'
  | 'path'
  | 'status-changes'
  | 'decisions';
type WorkflowGraphInspectableElement =
  | WorkflowReadOnlyGraphNode
  | WorkflowReadOnlyGraphEdge;

interface WorkflowVersionReferenceCodes {
  readonly stateCodes: string[];
  readonly decisionKindCodes: string[];
  readonly decisionOutcomeCodes: string[];
  readonly decisionReactionCodes: string[];
  readonly roleCodes: string[];
  readonly queueCodes: string[];
  readonly managerNotificationTargetCodes: string[];
  readonly externalNotificationTargetCodes: string[];
}

@Component({
  selector: 'phms-platform-workflow-version-workspace',
  standalone: true,
  imports: [
    WorkflowEntryPointsComponent,
    HearingEligibilityTestComponent,
    UiButtonDirective,
    UiTabGroupComponent,
    CommonModule,
    WorkflowExecutionPanelComponent,
    WorkspaceHostPreviewComponent,
    WorkflowReadOnlyGraphComponent,
    PlatformWorkflowDraftDecisionEditorComponent,
    PlatformWorkflowDraftEdgeEditorComponent,
    PlatformWorkflowDraftTransitionEditorComponent,
    WorkflowAssignmentPolicyComponent,
    WorkflowDefinitionLifecycleActionsComponent,
  ],
  templateUrl: './platform-workflow-version-workspace.component.html',
  styleUrl: './platform-workflow-version-workspace.component.scss',
})
export class PlatformWorkflowVersionWorkspaceComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private settingsFocusRequest = 0;
  private workFocusRequest = 0;
  private workSelection: string | null = null;
  private workOrigin: HTMLElement | null = null;
  private presentationFocusRequest = 0;
  private presentationSelection: string | null = null;
  private presentationOrigin: HTMLElement | null = null;
  private routingFocusRequest = 0;
  private routingSelection: string | null = null;
  private routingOrigin: HTMLElement | null = null;
  private statusFocusRequest = 0;
  private statusSelection: string | null = null;
  private statusOrigin: HTMLElement | null = null;
  private readonly statusViewGeneration = signal(0);
  private hearingMenuContext: string | null = null;
  private emittingDraftRequest: WorkflowPublishedDraftRequest | null = null;
  @ViewChild('draftEditButton')
  private draftEditButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('eligibilityMore')
  private eligibilityMore?: ElementRef<HTMLDetailsElement>;
  @ViewChild('publishedStatusHeading')
  private publishedStatusHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedStatusTaskHeading')
  private publishedStatusTaskHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedRoutingHeading')
  private publishedRoutingHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedRoutingTaskHeading')
  private publishedRoutingTaskHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedPresentationHeading')
  private publishedPresentationHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedPresentationTaskHeading')
  private publishedPresentationTaskHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedWorkHeading') private publishedWorkHeading?: ElementRef<HTMLElement>;
  @ViewChild('publishedWorkTaskHeading') private publishedWorkTaskHeading?: ElementRef<HTMLElement>;
  @ViewChild(HearingEligibilityTestComponent)
  eligibilityTests?: HearingEligibilityTestComponent;
  @Input() canTest = false;
  @Input() autoOpenHearing = false;
  private readonly definitionState =
    signal<WorkflowDefinitionVersionDetail | null>(null);
  private readonly lifecycleActions = viewChild(
    WorkflowDefinitionLifecycleActionsComponent,
  );

  @ViewChild('workspaceHeading')
  private workspaceHeading?: ElementRef<HTMLElement>;

  @ViewChild('backButton')
  private backButton?: ElementRef<HTMLButtonElement>;

  @ViewChild('mapInspectorHeading')
  private mapInspectorHeading?: ElementRef<HTMLElement>;

  @ViewChild('mapHeading')
  private mapHeading?: ElementRef<HTMLElement>;

  @ViewChild('setupHeading')
  private setupHeading?: ElementRef<HTMLElement>;

  @ViewChild('reviewHeading')
  private reviewHeading?: ElementRef<HTMLElement>;

  @ViewChild('reportedStatusSummary')
  private reportedStatusSummary?: ElementRef<HTMLElement>;

  @ViewChild(WorkflowDefinitionLifecycleActionsComponent, { read: ElementRef })
  private lifecycleActionsElement?: ElementRef<HTMLElement>;

  @ViewChild(PlatformWorkflowDraftDecisionEditorComponent)
  private decisionEditor?: PlatformWorkflowDraftDecisionEditorComponent;

  @Input({ required: true })
  set definition(value: WorkflowDefinitionVersionDetail | null) {
    const next = value;
    const current = this.definitionState();
    const changed =
      current?.id !== next?.id || current?.canonicalContentHash !== next?.canonicalContentHash ||
      current?.workflowCode !== next?.workflowCode ||
      current?.definitionVersionCode !== next?.definitionVersionCode;
    const settingsContext = this.publishedInspection()?.context;
    const workflowSettingsContext = this.publishedWorkflowSettings()?.context;
    const workContext = this.publishedWork()?.context;
    const presentationContext = this.publishedPresentation()?.context;
    const routingContext = this.publishedRouting()?.context;
    const statusContext = this.publishedStatus()?.context;
    this.definitionState.set(next);
    if (settingsContext !== this.publishedInspection()?.context) {
      this.settingsTask.set('general'); this.settingsFocusRequest++;
    }
    if (workflowSettingsContext !== this.publishedWorkflowSettings()?.context) {
      this.settingsFocusRequest++;
      this.closeHearingMore();
      this.technicalDocumentOpenContext.set(null);
      if (this.isWorkflowSettingsTask(this.settingsTask()))
        this.settingsTask.set('general');
    }
    if (!changed && workContext !== this.publishedWork()?.context) this.resetPublishedWork();
    if (!changed && presentationContext !== this.publishedPresentation()?.context)
      this.resetPublishedPresentation();
    if (!changed && routingContext !== this.publishedRouting()?.context)
      this.resetPublishedRouting();
    if (!changed && statusContext !== this.publishedStatus()?.context)
      this.resetPublishedStatus();
    if (changed) {
      this.diagnosticsOpen.set(false);
      this.diagnosticsLoaded.set(false);
      this.reportedStatusOpen.set(false);
      this.resetMapContext();
    }
  }

  get definition(): WorkflowDefinitionVersionDetail | null {
    return this.definitionState();
  }

  private activeViewValue: WorkflowVersionWorkbenchView = 'map';
  @Input()
  set activeView(value: WorkflowVersionWorkbenchView) {
    if (value !== this.activeViewValue) {
      this.resetPublishedStatus();
      this.closeHearingMore();
      this.technicalDocumentOpenContext.set(null);
      this.statusViewGeneration.update((generation) => generation + 1);
    }
    this.activeViewValue = value;
  }
  get activeView(): WorkflowVersionWorkbenchView {
    return this.activeViewValue;
  }
  @Input() newlyPublished = false;
  @Input() canEdit = false;
  @Input() loading = false;
  @Input() entryPointsVisible = true;
  @Input() interactionBlocked = false;

  @Output() readonly activeViewChange =
    new EventEmitter<WorkflowVersionWorkbenchView>();
  @Output() readonly backRequested = new EventEmitter<void>();
  @Output() readonly lifecycleChanged = new EventEmitter<void>();
  @Output() readonly editRequested =
    new EventEmitter<WorkflowPublishedDraftRequest>();

  readonly diagnosticsOpen = signal(false);
  readonly lifecycleInProgress = signal(false);
  readonly diagnosticsLoaded = signal(false);
  readonly reportedStatusOpen = signal(false);
  readonly settingsTask = signal<WorkflowPublishedSettingsTask>('general');
  private readonly technicalDocumentOpenContext = signal<string | null>(null);
  readonly settingsTabs = [
    { id: 'general', label: 'General' }, { id: 'data', label: 'Data between steps' },
    { id: 'assignment', label: 'Assignment' }, { id: 'technical', label: 'Technical' },
    { id: 'publicReferences', label: 'Public references' },
    { id: 'eventAutomation', label: 'Events' },
    { id: 'dependencies', label: 'Modules' },
    { id: 'navigation', label: 'Staff navigation' },
  ];
  readonly settingsTaskLabel = computed(() => this.settingsTabs.find(tab => tab.id === this.settingsTask())!.label);
  readonly publishedInspection = computed(() => {
    const definition = this.definitionState(); return definition ? readWorkflowPublishedInspection(definition) : null;
  });
  readonly publishedWorkflowSettings = computed(() => {
    const source = this.definitionState();
    return source
      ? readWorkflowPublishedWorkflowSettingsInspection(source)
      : null;
  });
  get hearingSourceContext(): string | null {
    const source = this.definitionState();
    return source && this.canTest && source.subjectTypeCode === 'hearing_cycle'
      ? JSON.stringify([
          this.publishedWorkflowSettings()?.context,
          this.activeView,
          this.statusViewGeneration(),
        ])
      : null;
  }
  readonly currentHearingSourceContext = (): string | null => {
    const host = this.host.nativeElement;
    if (
      this.destroyRef.destroyed ||
      this.loading ||
      this.interactionBlocked ||
      this.lifecycleInProgress() ||
      !host.isConnected ||
      host.closest('[hidden],[inert]')
    )
      return null;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return null;
    }
    // Only the child's private dialog witness may tolerate Material's temporary background aria-hidden.
    return this.hearingSourceContext;
  };

  get availabilitySourceContext(): string {
    return this.publishedWorkflowSettings()?.context ?? '';
  }
  get availabilityOwnerContext(): string {
    return JSON.stringify([this.activeView, this.statusViewGeneration()]);
  }
  readonly currentAvailabilitySourceContext = (): string | null => {
    const host = this.host.nativeElement;
    if (
      !this.definitionState() ||
      this.destroyRef.destroyed ||
      this.activeView !== 'review' ||
      this.loading ||
      this.interactionBlocked ||
      !host.isConnected ||
      host.closest('[hidden],[inert]')
    )
      return null;
    for (let node: HTMLElement | null = host; node; node = node.parentElement) {
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return null;
    }
    // Action-specific permission and own-confirmation aria checks remain with the lifecycle owner.
    return this.availabilitySourceContext;
  };
  readonly currentAvailabilityOwnerContext = (): string | null =>
    this.currentAvailabilitySourceContext() === null
      ? null
      : this.availabilityOwnerContext;
  readonly draftEditEntry = computed<WorkflowPublishedDraftEntry | null>(() => {
    const source = this.definitionState(),
      action = workflowPublishedDraftAction(source?.draftActionCode);
    if (!source || !action) return null;
    return {
      source,
      request: {
        definitionVersionId: source.id,
        workflowCode: source.workflowCode,
        definitionVersionCode: source.definitionVersionCode,
        sourceContext: this.publishedWorkflowSettings()?.context ?? '',
        actionCode: action,
        actionContext: JSON.stringify([
          source.draftActionCode,
          Object.hasOwn(source, 'editableDraftId'),
          source.editableDraftId,
        ]),
        view: this.activeView,
        ownerContext: this.availabilityOwnerContext,
      },
    };
  });

  readonly publishedWorkflowSettingsSection = computed(() => {
    const task = this.settingsTask();
    if (!this.isWorkflowSettingsTask(task)) return null;
    const settings = this.publishedWorkflowSettings();
    return settings?.state === 'available' ? settings.sections[task] : null;
  });
  readonly technicalDocumentEntry =
    computed<WorkflowPublishedTechnicalDocumentEntry | null>(() => {
      const source = this.publishedWorkflowSettings(),
        viewGeneration = this.statusViewGeneration();
      return source?.state === 'available' &&
        source.originalDocument !== null &&
        this.activeView === 'setup' &&
        this.settingsTask() === 'technical' &&
        !(this.statusOpen() && this.statusView() === 'setup')
        ? { context: source.context, viewGeneration }
        : null;
    });
  readonly technicalDocumentOpen = computed(() => {
    const entry = this.technicalDocumentEntry();
    return (
      entry !== null && this.technicalDocumentOpenContext() === entry.context
    );
  });

  readonly workOpen = signal(false);
  readonly workTarget = signal<
    WorkflowPublishedWorkInspection['target'] | null
  >(null);
  readonly workTask = signal<WorkflowPublishedWorkTask>('responsibility');
  readonly workTabs = [
    { id: 'responsibility', label: 'Responsibility' },
    { id: 'requirements', label: 'Requirements' },
    { id: 'forms', label: 'Forms' },
    { id: 'actions', label: 'Actions' },
    { id: 'timers', label: 'Timers' },
    { id: 'relatedWork', label: 'Related work' },
    { id: 'progress', label: 'Progress and labels' },
  ];
  readonly workTaskLabel = computed(
    () => this.workTabs.find((tab) => tab.id === this.workTask())!.label,
  );
  readonly publishedWork = computed(() => {
    const source = this.definitionState(),
      target = this.workTarget();
    return source && target
      ? readWorkflowPublishedWorkInspection(source, target)
      : null;
  });
  readonly publishedStepPolicies = computed(() => {
    const source = this.definitionState(),
      target = this.workTarget();
    return source && target
      ? readWorkflowPublishedStepPolicyInspection(source, target)
      : null;
  });
  readonly publishedProgress = computed(() => {
    const source = this.definitionState(),
      target = this.workTarget();
    return source && target
      ? readWorkflowPublishedProgressInspection(source, target)
      : null;
  });
  readonly publishedProgressSections = computed(() => {
    const progress = this.publishedProgress(),
      work = this.publishedWork();
    return work?.state === 'available' &&
      progress?.state === 'available' &&
      progress.context === work.context
      ? [progress.sections.progress, progress.sections.stepLabels]
      : null;
  });
  readonly publishedWorkTaskSection = computed(() => {
    const task = this.workTask(),
      work = this.publishedWork();
    if (task === 'progress') return null;
    if (task === 'timers' || task === 'relatedWork') {
      const policies = this.publishedStepPolicies();
      return work?.state === 'available' &&
        policies?.state === 'available' &&
        policies.context === work.context
        ? policies.sections[task]
        : null;
    }
    return work?.sections[task] ?? null;
  });
  readonly workEntry = computed<WorkflowPublishedWorkEntry | null>(() => {
    const source = this.definitionState(),
      admission = this.publishedInspection(),
      node = this.selectedNode();
    const selection = this.mapSelection();
    if (
      !source ||
      admission?.state !== 'available' ||
      selection?.kind !== 'node' ||
      node?.kind !== 'human-step' ||
      node.sourceIndex === null ||
      !Number.isInteger(node.sourceIndex)
    )
      return null;
    return {
      context: JSON.stringify([admission.context, source.definitionDocument]),
      selection: JSON.stringify(selection),
      target: { nodeIndex: node.sourceIndex, nodeId: node.sourceId },
    };
  });

  readonly presentationOpen = signal(false);
  readonly presentationTarget = signal<
    WorkflowPublishedPresentationInspection['target'] | null
  >(null);
  readonly presentationTask =
    signal<WorkflowPublishedPresentationSectionId>('staffPage');
  readonly presentationTabs = [
    { id: 'staffPage', label: 'Staff page' },
    { id: 'dashboards', label: 'Dashboards' },
  ];
  readonly presentationTaskLabel = computed(
    () =>
      this.presentationTabs.find((tab) => tab.id === this.presentationTask())!
        .label,
  );
  readonly publishedPresentation = computed(() => {
    const source = this.definitionState(),
      target = this.presentationTarget();
    return source && target
      ? readWorkflowPublishedPresentationInspection(source, target)
      : null;
  });
  readonly presentationEntry =
    computed<WorkflowPublishedPresentationEntry | null>(() => {
      const entry = this.workEntry();
      return entry ? { ...entry, target: { ...entry.target } } : null;
    });
  readonly routingOpen = signal(false);
  readonly routingTarget = signal<WorkflowPublishedRoutingTarget | null>(null);
  readonly routingTask = signal<WorkflowPublishedRoutingTask>('paths');
  readonly routingTabs = [
    { id: 'paths', label: 'Paths' },
    { id: 'results', label: 'Results' },
  ];
  readonly routingTaskLabel = computed(
    () => this.routingTabs.find((tab) => tab.id === this.routingTask())!.label,
  );
  readonly publishedRouting = computed(() => {
    const source = this.definitionState(),
      target = this.routingTarget();
    return source && target
      ? readWorkflowPublishedRoutingInspection(source, target)
      : null;
  });
  readonly publishedRoutingSections = computed(() => {
    const routing = this.publishedRouting();
    if (routing?.state !== 'available') return [];
    return this.routingTask() === 'paths'
      ? [routing.sections.paths, routing.sections.condition]
      : [
          routing.sections.commandResults,
          routing.sections.automation,
          routing.sections.terminalResult,
        ];
  });
  readonly routingEntry = computed<WorkflowPublishedRoutingEntry | null>(() => {
    const source = this.definitionState(),
      admission = this.publishedInspection(),
      selection = this.mapSelection(),
      selected = this.selectedGraphElement();
    if (
      !source ||
      admission?.state !== 'available' ||
      !selection ||
      !selected ||
      selected.sourceIndex === null ||
      !Number.isSafeInteger(selected.sourceIndex)
    )
      return null;
    const rows =
      selection.kind === 'node'
        ? source.definitionDocument?.nodes
        : source.definitionDocument?.edges;
    const index = selected.sourceIndex;
    if (!Array.isArray(rows) || index < 0 || index >= rows.length) return null;
    const raw = rows[index];
    const id =
      raw &&
      typeof raw === 'object' &&
      !Array.isArray(raw) &&
      typeof raw.id === 'string'
        ? raw.id
        : null;
    return {
      context: JSON.stringify([admission.context, source.definitionDocument]),
      selection: JSON.stringify(selection),
      target:
        selection.kind === 'node'
          ? { kind: 'node', nodeIndex: index, nodeId: id }
          : { kind: 'edge', edgeIndex: index, edgeId: id },
    };
  });
  readonly statusOpen = signal(false);
  readonly statusView = signal<WorkflowPublishedStatusView | null>(null);
  readonly statusTarget = signal<WorkflowPublishedStatusTarget | null>(null);
  readonly statusTask =
    signal<WorkflowPublishedStatusSectionId>('businessStatus');
  readonly statusTabs = [
    { id: 'businessStatus', label: 'Business labels' },
    { id: 'legacyTransitions', label: 'Status changes' },
    { id: 'legacyDecisions', label: 'Decisions' },
  ];
  readonly statusTaskLabel = computed(
    () => this.statusTabs.find((tab) => tab.id === this.statusTask())!.label,
  );
  readonly publishedStatus = computed(() => {
    const source = this.definitionState(),
      target = this.statusTarget();
    return source && target
      ? readWorkflowPublishedStatusInspection(source, target)
      : null;
  });
  readonly workflowStatusEntry = computed<WorkflowPublishedStatusEntry | null>(
    () => {
      const source = this.definitionState(),
        admission = this.publishedInspection();
      if (!source || admission?.state !== 'available') return null;
      return {
        context: JSON.stringify([
          admission.context,
          source.definitionDocument,
          this.statusViewGeneration(),
        ]),
        selection: JSON.stringify(this.settingsTask()),
        view: 'setup',
        target: { kind: 'workflow' },
      };
    },
  );
  readonly nodeStatusEntry = computed<WorkflowPublishedStatusEntry | null>(
    () => {
      const source = this.definitionState(),
        admission = this.publishedInspection(),
        selection = this.mapSelection(),
        selected = this.selectedGraphElement();
      if (
        !source ||
        admission?.state !== 'available' ||
        selection?.kind !== 'node' ||
        !selected ||
        selected.sourceIndex === null ||
        !Number.isSafeInteger(selected.sourceIndex)
      )
        return null;
      const nodes = source.definitionDocument?.nodes,
        index = selected.sourceIndex;
      if (!Array.isArray(nodes) || index < 0 || index >= nodes.length)
        return null;
      const raw = nodes[index];
      const id =
        raw &&
        typeof raw === 'object' &&
        !Array.isArray(raw) &&
        typeof raw.id === 'string'
          ? raw.id
          : null;
      return {
        context: JSON.stringify([
          admission.context,
          source.definitionDocument,
          this.statusViewGeneration(),
        ]),
        selection: JSON.stringify([selection, this.inspectorPane()]),
        view: 'map',
        target: { kind: 'node', nodeIndex: index, nodeId: id },
      };
    },
  );
  @ViewChild('savedSettingsHeading') private savedSettingsHeading?: ElementRef<HTMLElement>;
  readonly reviewTimeZone = new Intl.DateTimeFormat().resolvedOptions()
    .timeZone;
  readonly reviewPresentation = computed(() => {
    const definition = this.definitionState();
    const preview = this.lifecycleActions()?.versionChange();
    return definition
      ? workflowVersionReviewPresentation(
          definition,
          preview?.definitionVersionId === definition.id ? preview : null,
        )
      : null;
  });

  selectSettingsTask(
    task: string,
    context: string,
    savedWorkflowContext?: string,
  ): void {
    const savedTask = this.isWorkflowSettingsTask(task);
    const expectedContext = savedTask ? savedWorkflowContext : context;
    const currentContext = () =>
      savedTask
        ? this.publishedWorkflowSettings()?.context
        : this.publishedInspection()?.context;
    if (
      (this.statusOpen() && this.statusView() === 'setup') ||
      !this.settingsTabs.some((tab) => tab.id === task) ||
      this.activeView !== 'setup' ||
      this.loading ||
      this.interactionBlocked ||
      context !== this.publishedInspection()?.context ||
      typeof expectedContext !== 'string' ||
      expectedContext !== currentContext() ||
      (savedTask &&
        (this.destroyRef.destroyed ||
          this.lifecycleInProgress() ||
          !this.host.nativeElement.isConnected)) ||
      this.host.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]')
    )
      return;
    if (task !== 'technical') this.technicalDocumentOpenContext.set(null);
    this.settingsTask.set(task as WorkflowPublishedSettingsTask);
    const request = ++this.settingsFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        this.destroyRef.destroyed ||
        request !== this.settingsFocusRequest ||
        expectedContext !== currentContext() ||
        this.activeView !== 'setup' ||
        this.settingsTask() !== task ||
        this.loading ||
        this.interactionBlocked ||
        (savedTask && this.lifecycleInProgress()) ||
        !host.isConnected ||
        host.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      if (
        active !== before &&
        active !== host.ownerDocument.body &&
        active?.isConnected &&
        !active.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      const heading = this.savedSettingsHeading?.nativeElement;
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.settingsFocusRequest &&
        expectedContext === currentContext() &&
        !this.destroyRef.destroyed &&
        this.activeView === 'setup' &&
        this.settingsTask() === task &&
        !this.loading &&
        !this.interactionBlocked &&
        (!savedTask || !this.lifecycleInProgress()) &&
        host.isConnected &&
        heading.isConnected &&
        !heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private isWorkflowSettingsTask(
    task: string,
  ): task is WorkflowPublishedWorkflowSettingsSectionId {
    return (
      task === 'publicReferences' ||
      task === 'eventAutomation' ||
      task === 'dependencies' ||
      task === 'navigation'
    );
  }

  toggleHearingMore(
    event: Event,
    context: string,
    more: HTMLDetailsElement,
  ): void {
    event.preventDefault();
    if (!this.hearingMoreCurrent(context, more)) {
      this.reconcileHearingMore(more);
      return;
    }
    if (more.open) {
      this.closeHearingMore();
      return;
    }
    this.hearingMenuContext = context;
    more.open = true;
  }

  reconcileHearingMore(more: HTMLDetailsElement): void {
    if (this.eligibilityMore?.nativeElement !== more) return;
    if (
      !more.open ||
      this.hearingMenuContext === null ||
      !this.hearingMoreCurrent(this.hearingMenuContext, more)
    )
      this.closeHearingMore();
  }

  dismissHearingMore(
    event: Event,
    context: string,
    more: HTMLDetailsElement,
  ): void {
    if (!more.open || !this.hearingMoreCurrent(context, more)) return;
    event.preventDefault();
    event.stopPropagation();
    this.closeHearingMore();
    more.querySelector<HTMLElement>('summary')?.focus({ preventScroll: true });
  }

  openHearingTest(
    context: string,
    more: HTMLDetailsElement,
    item: HTMLButtonElement,
  ): void {
    const child = this.eligibilityTests,
      origin = more.querySelector<HTMLElement>('summary');
    if (
      !child ||
      !origin ||
      !more.open ||
      this.hearingMenuContext !== context ||
      !this.hearingMoreCurrent(context, more) ||
      more.querySelector('[data-run-hearing-test]') !== item ||
      item.matches(':disabled') ||
      child.sourceContext !== context
    )
      return;
    this.closeHearingMore();
    void child.open(origin);
  }

  private hearingMoreCurrent(
    context: string,
    more: HTMLDetailsElement,
  ): boolean {
    return (
      context === this.currentHearingSourceContext() &&
      this.eligibilityMore?.nativeElement === more &&
      more.isConnected &&
      !more.closest('[hidden],[inert],[aria-hidden="true"]')
    );
  }

  private closeHearingMore(): void {
    this.hearingMenuContext = null;
    const more = this.eligibilityMore?.nativeElement;
    if (more?.open) more.open = false;
  }

  toggleTechnicalDocument(
    event: Event,
    entry: WorkflowPublishedTechnicalDocumentEntry,
    details: HTMLDetailsElement,
  ): void {
    event.preventDefault();
    if (this.technicalDocumentCurrent(entry, details)) {
      this.technicalDocumentOpenContext.set(
        this.technicalDocumentOpenContext() === entry.context
          ? null
          : entry.context,
      );
    }
    this.reconcileTechnicalDocument(details);
  }

  reconcileTechnicalDocument(details: HTMLDetailsElement): void {
    const entry = this.technicalDocumentEntry();
    const open =
      entry !== null &&
      this.technicalDocumentCurrent(entry, details) &&
      this.technicalDocumentOpen();
    if (details.open !== open) details.open = open;
  }

  private technicalDocumentCurrent(
    entry: WorkflowPublishedTechnicalDocumentEntry,
    details: HTMLDetailsElement,
  ): boolean {
    const host = this.host.nativeElement;
    return (
      !!entry &&
      entry === this.technicalDocumentEntry() &&
      entry.context === this.publishedWorkflowSettings()?.context &&
      entry.viewGeneration === this.statusViewGeneration() &&
      this.activeView === 'setup' &&
      this.settingsTask() === 'technical' &&
      !(this.statusOpen() && this.statusView() === 'setup') &&
      !this.destroyRef.destroyed &&
      !this.loading &&
      !this.interactionBlocked &&
      !this.lifecycleInProgress() &&
      host.isConnected &&
      details.isConnected &&
      host.querySelector('[data-published-original-document]') === details &&
      !details.closest('[hidden],[inert],[aria-hidden="true"]')
    );
  }

  openPublishedWork(entry: WorkflowPublishedWorkEntry): void {
    const current = this.workEntry();
    if (
      !entry ||
      !current ||
      this.presentationOpen() ||
      this.routingOpen() ||
      this.statusOpen() ||
      !this.workFrameCurrent() ||
      entry.context !== current.context ||
      entry.selection !== current.selection ||
      entry.target.nodeIndex !== current.target.nodeIndex ||
      entry.target.nodeId !== current.target.nodeId
    )
      return;
    const focused = this.host.nativeElement.ownerDocument.activeElement;
    this.workOrigin =
      focused instanceof HTMLElement &&
      focused.matches('[data-inspect-published-work]') &&
      this.host.nativeElement.contains(focused)
        ? focused
        : null;
    this.workSelection = current.selection;
    this.workTarget.set({ ...current.target });
    this.workTask.set('responsibility');
    this.workOpen.set(true);
    const context = this.publishedWork()?.context;
    if (context)
      this.queuePublishedWorkFocus(
        context,
        'responsibility',
        () => this.publishedWorkHeading?.nativeElement,
      );
  }

  selectPublishedWorkTask(task: string, context: string): void {
    if (
      !this.workTabs.some((tab) => tab.id === task) ||
      !this.workContextCurrent(context)
    )
      return;
    this.workTask.set(task as WorkflowPublishedWorkTask);
    this.queuePublishedWorkFocus(
      context,
      this.workTask(),
      () => this.publishedWorkTaskHeading?.nativeElement,
    );
  }

  backToPublishedMap(context: string): void {
    if (!this.workContextCurrent(context)) return;
    const selection = this.workSelection,
      origin = this.workOrigin,
      source = this.workEntry()?.context;
    this.resetPublishedWork();
    const request = ++this.workFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        this.destroyRef.destroyed ||
        request !== this.workFocusRequest ||
        this.workOpen() ||
        !this.workFrameCurrent() ||
        source !== this.workEntry()?.context ||
        selection !== JSON.stringify(this.mapSelection()) ||
        this.newerWorkFocus(active, before)
      )
        return;
      const target =
        origin?.isConnected &&
        !origin.closest('[hidden],[inert],[aria-hidden="true"]')
          ? origin
          : this.mapInspectorHeading?.nativeElement;
      if (
        !target?.isConnected ||
        target.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      target.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === target &&
        request === this.workFocusRequest &&
        this.workFrameCurrent() &&
        source === this.workEntry()?.context &&
        selection === JSON.stringify(this.mapSelection())
      )
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  publishedWorkSectionMessage(
    section: Pick<WorkflowPublishedWorkSection, 'state' | 'presence'>,
  ): string {
    if (section.state === 'absent')
      return section.presence === 'null'
        ? 'The saved collection is explicitly null.'
        : 'This section is not declared in the saved document.';
    if (section.state === 'empty') return 'The saved list is empty.';
    if (section.state === 'retained-unknown')
      return 'Some saved settings need inspection. Their original values are retained below.';
    if (section.state === 'unavailable')
      return 'This saved section is unavailable.';
    return '';
  }

  private workFrameCurrent(): boolean {
    const host = this.host.nativeElement;
    return (
      !this.destroyRef.destroyed &&
      this.activeView === 'map' &&
      !this.loading &&
      !this.interactionBlocked &&
      !this.lifecycleInProgress() &&
      host.isConnected &&
      !host.closest('[hidden],[inert],[aria-hidden="true"]')
    );
  }

  private workContextCurrent(context: string): boolean {
    return (
      this.workOpen() &&
      this.workFrameCurrent() &&
      context === this.publishedWork()?.context &&
      this.workSelection === JSON.stringify(this.mapSelection())
    );
  }

  private newerWorkFocus(
    active: Element | null,
    before: Element | null,
  ): boolean {
    return (
      active !== before &&
      active !== this.host.nativeElement.ownerDocument.body &&
      !!active?.isConnected &&
      !active.closest('[hidden],[inert],[aria-hidden="true"]')
    );
  }

  private queuePublishedWorkFocus(
    context: string,
    task: WorkflowPublishedWorkTask,
    target: () => HTMLElement | undefined,
  ): void {
    const request = ++this.workFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        request !== this.workFocusRequest ||
        !this.workContextCurrent(context) ||
        task !== this.workTask() ||
        this.newerWorkFocus(active, before)
      )
        return;
      const heading = target();
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.workFocusRequest &&
        this.workContextCurrent(context) &&
        task === this.workTask() &&
        heading.isConnected &&
        !heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private resetPublishedWork(): void {
    this.workFocusRequest++;
    this.workOpen.set(false);
    this.workTarget.set(null);
    this.workTask.set('responsibility');
    this.workSelection = null;
    this.workOrigin = null;
  }

  openPublishedPresentation(
    entry: WorkflowPublishedPresentationEntry,
    task: WorkflowPublishedPresentationSectionId,
  ): void {
    const current = this.presentationEntry();
    if (
      !entry ||
      !current ||
      this.workOpen() ||
      this.presentationOpen() ||
      this.routingOpen() ||
      this.statusOpen() ||
      !this.workFrameCurrent() ||
      !this.presentationTabs.some((tab) => tab.id === task) ||
      entry.context !== current.context ||
      entry.selection !== current.selection ||
      entry.target.nodeIndex !== current.target.nodeIndex ||
      entry.target.nodeId !== current.target.nodeId
    )
      return;
    const focused = this.host.nativeElement.ownerDocument.activeElement;
    this.presentationOrigin =
      focused instanceof HTMLElement &&
      focused.matches('[data-inspect-published-presentation]') &&
      this.host.nativeElement.contains(focused)
        ? focused
        : null;
    this.workFocusRequest++;
    this.presentationSelection = current.selection;
    this.presentationTarget.set({ ...current.target });
    this.presentationTask.set(task);
    this.presentationOpen.set(true);
    const context = this.publishedPresentation()?.context;
    if (context)
      this.queuePublishedPresentationFocus(
        context,
        task,
        () => this.publishedPresentationHeading?.nativeElement,
      );
  }

  selectPublishedPresentationTask(task: string, context: string): void {
    if (
      !this.presentationTabs.some((tab) => tab.id === task) ||
      !this.presentationContextCurrent(context)
    )
      return;
    this.presentationTask.set(task as WorkflowPublishedPresentationSectionId);
    this.queuePublishedPresentationFocus(
      context,
      this.presentationTask(),
      () => this.publishedPresentationTaskHeading?.nativeElement,
    );
  }

  backToPublishedPresentationMap(context: string): void {
    if (!this.presentationContextCurrent(context)) return;
    const selection = this.presentationSelection,
      origin = this.presentationOrigin,
      source = this.presentationEntry()?.context;
    this.resetPublishedPresentation();
    const request = ++this.presentationFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        this.destroyRef.destroyed ||
        request !== this.presentationFocusRequest ||
        this.presentationOpen() ||
        this.workOpen() ||
        !this.workFrameCurrent() ||
        source !== this.presentationEntry()?.context ||
        selection !== JSON.stringify(this.mapSelection()) ||
        this.newerWorkFocus(active, before)
      )
        return;
      const target =
        origin?.isConnected &&
        !origin.closest('[hidden],[inert],[aria-hidden="true"]')
          ? origin
          : this.mapInspectorHeading?.nativeElement;
      if (
        !target?.isConnected ||
        target.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      target.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === target &&
        request === this.presentationFocusRequest &&
        this.workFrameCurrent() &&
        !this.presentationOpen() &&
        !this.workOpen() &&
        source === this.presentationEntry()?.context &&
        selection === JSON.stringify(this.mapSelection())
      )
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private presentationContextCurrent(context: string): boolean {
    return (
      this.presentationOpen() &&
      this.workFrameCurrent() &&
      context === this.publishedPresentation()?.context &&
      this.presentationSelection === JSON.stringify(this.mapSelection())
    );
  }

  private queuePublishedPresentationFocus(
    context: string,
    task: WorkflowPublishedPresentationSectionId,
    target: () => HTMLElement | undefined,
  ): void {
    const request = ++this.presentationFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        request !== this.presentationFocusRequest ||
        !this.presentationContextCurrent(context) ||
        task !== this.presentationTask() ||
        this.newerWorkFocus(active, before)
      )
        return;
      const heading = target();
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.presentationFocusRequest &&
        this.presentationContextCurrent(context) &&
        task === this.presentationTask() &&
        heading.isConnected &&
        !heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private resetPublishedPresentation(): void {
    this.presentationFocusRequest++;
    this.presentationOpen.set(false);
    this.presentationTarget.set(null);
    this.presentationTask.set('staffPage');
    this.presentationSelection = null;
    this.presentationOrigin = null;
  }
  openPublishedRouting(entry: WorkflowPublishedRoutingEntry): void {
    const current = this.routingEntry();
    if (
      !entry ||
      !current ||
      this.workOpen() ||
      this.presentationOpen() ||
      this.routingOpen() ||
      this.statusOpen() ||
      !this.workFrameCurrent() ||
      entry.context !== current.context ||
      entry.selection !== current.selection ||
      JSON.stringify(entry.target) !== JSON.stringify(current.target)
    )
      return;
    const active = this.host.nativeElement.ownerDocument.activeElement;
    this.routingOrigin =
      active instanceof HTMLElement &&
      active.matches('[data-inspect-published-routing]') &&
      this.host.nativeElement.contains(active)
        ? active
        : null;
    this.workFocusRequest++;
    this.presentationFocusRequest++;
    this.routingSelection = current.selection;
    this.routingTarget.set({ ...current.target });
    this.routingTask.set('paths');
    this.routingOpen.set(true);
    const context = this.publishedRouting()?.context;
    if (context)
      this.queuePublishedRoutingFocus(
        context,
        'paths',
        () => this.publishedRoutingHeading?.nativeElement,
      );
  }

  selectPublishedRoutingTask(task: string, context: string): void {
    if (
      !this.routingTabs.some((tab) => tab.id === task) ||
      !this.routingContextCurrent(context)
    )
      return;
    this.routingTask.set(task as WorkflowPublishedRoutingTask);
    this.queuePublishedRoutingFocus(
      context,
      this.routingTask(),
      () => this.publishedRoutingTaskHeading?.nativeElement,
    );
  }

  backToPublishedRoutingMap(context: string): void {
    if (!this.routingContextCurrent(context)) return;
    const selection = this.routingSelection,
      origin = this.routingOrigin,
      source = this.routingEntry()?.context;
    this.resetPublishedRouting();
    const request = ++this.routingFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        this.destroyRef.destroyed ||
        request !== this.routingFocusRequest ||
        this.routingOpen() ||
        this.workOpen() ||
        this.presentationOpen() ||
        !this.workFrameCurrent() ||
        source !== this.routingEntry()?.context ||
        selection !== JSON.stringify(this.mapSelection()) ||
        this.newerWorkFocus(active, before)
      )
        return;
      const target =
        origin?.isConnected &&
        !origin.closest('[hidden],[inert],[aria-hidden="true"]')
          ? origin
          : this.mapInspectorHeading?.nativeElement;
      if (
        !target?.isConnected ||
        target.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      target.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === target &&
        request === this.routingFocusRequest &&
        this.workFrameCurrent() &&
        !this.routingOpen() &&
        !this.workOpen() &&
        !this.presentationOpen() &&
        source === this.routingEntry()?.context &&
        selection === JSON.stringify(this.mapSelection())
      )
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private routingContextCurrent(context: string): boolean {
    return (
      this.routingOpen() &&
      this.workFrameCurrent() &&
      context === this.publishedRouting()?.context &&
      this.routingSelection === JSON.stringify(this.mapSelection()) &&
      JSON.stringify(this.routingTarget()) ===
        JSON.stringify(this.routingEntry()?.target)
    );
  }

  private queuePublishedRoutingFocus(
    context: string,
    task: WorkflowPublishedRoutingTask,
    target: () => HTMLElement | undefined,
  ): void {
    const request = ++this.routingFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        request !== this.routingFocusRequest ||
        !this.routingContextCurrent(context) ||
        task !== this.routingTask() ||
        this.newerWorkFocus(active, before)
      )
        return;
      const heading = target();
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.routingFocusRequest &&
        this.routingContextCurrent(context) &&
        task === this.routingTask() &&
        heading.isConnected &&
        !heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private resetPublishedRouting(): void {
    this.routingFocusRequest++;
    this.routingOpen.set(false);
    this.routingTarget.set(null);
    this.routingTask.set('paths');
    this.routingSelection = null;
    this.routingOrigin = null;
  }

  openPublishedStatus(entry: WorkflowPublishedStatusEntry): void {
    const current =
      entry?.target?.kind === 'workflow'
        ? this.workflowStatusEntry()
        : this.nodeStatusEntry();
    if (
      !entry ||
      !current ||
      this.statusOpen() ||
      entry.view !== current.view ||
      entry.view !== this.activeView ||
      !this.statusFrameCurrent(entry.view) ||
      (entry.view === 'map' &&
        (this.workOpen() || this.presentationOpen() || this.routingOpen())) ||
      entry.context !== current.context ||
      entry.selection !== current.selection ||
      JSON.stringify(entry.target) !== JSON.stringify(current.target)
    )
      return;
    const active = this.host.nativeElement.ownerDocument.activeElement;
    this.statusOrigin =
      active instanceof HTMLElement &&
      active.matches('[data-inspect-published-status]') &&
      this.host.nativeElement.contains(active)
        ? active
        : null;
    this.settingsFocusRequest++;
    this.workFocusRequest++;
    this.presentationFocusRequest++;
    this.routingFocusRequest++;
    this.statusSelection = current.selection;
    this.statusView.set(current.view);
    this.statusTarget.set({ ...current.target });
    this.statusTask.set('businessStatus');
    this.statusOpen.set(true);
    const context = this.publishedStatus()?.context;
    if (context)
      this.queuePublishedStatusFocus(
        context,
        'businessStatus',
        () => this.publishedStatusHeading?.nativeElement,
      );
  }

  selectPublishedStatusTask(task: string, context: string): void {
    if (
      !this.statusTabs.some((tab) => tab.id === task) ||
      !this.statusContextCurrent(context)
    )
      return;
    this.statusTask.set(task as WorkflowPublishedStatusSectionId);
    this.queuePublishedStatusFocus(
      context,
      this.statusTask(),
      () => this.publishedStatusTaskHeading?.nativeElement,
    );
  }

  backFromPublishedStatus(context: string): void {
    if (!this.statusContextCurrent(context)) return;
    const target = this.statusTarget()!,
      view = this.statusView()!,
      origin = this.statusOrigin,
      selection = this.statusSelection;
    const entry = () =>
      target.kind === 'workflow'
        ? this.workflowStatusEntry()
        : this.nodeStatusEntry();
    const source = entry()?.context;
    this.resetPublishedStatus();
    const request = ++this.statusFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        this.destroyRef.destroyed ||
        request !== this.statusFocusRequest ||
        this.statusOpen() ||
        !this.statusFrameCurrent(view) ||
        (view === 'map' &&
          (this.workOpen() || this.presentationOpen() || this.routingOpen())) ||
        source !== entry()?.context ||
        selection !== entry()?.selection ||
        this.newerWorkFocus(active, before)
      )
        return;
      const fallback =
        view === 'map'
          ? this.mapInspectorHeading?.nativeElement
          : this.setupHeading?.nativeElement;
      const heading =
        origin?.isConnected &&
        !origin.closest('[hidden],[inert],[aria-hidden="true"]')
          ? origin
          : fallback;
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.statusFocusRequest &&
        !this.statusOpen() &&
        this.statusFrameCurrent(view) &&
        source === entry()?.context &&
        selection === entry()?.selection &&
        (view !== 'map' ||
          (!this.workOpen() && !this.presentationOpen() && !this.routingOpen()))
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private statusFrameCurrent(view: WorkflowPublishedStatusView): boolean {
    const host = this.host.nativeElement;
    return (
      !this.destroyRef.destroyed &&
      this.activeView === view &&
      !this.loading &&
      !this.interactionBlocked &&
      !this.lifecycleInProgress() &&
      host.isConnected &&
      !host.closest('[hidden],[inert],[aria-hidden="true"]')
    );
  }

  private statusContextCurrent(context: string): boolean {
    const target = this.statusTarget(),
      view = this.statusView();
    const entry =
      target?.kind === 'workflow'
        ? this.workflowStatusEntry()
        : this.nodeStatusEntry();
    return (
      this.statusOpen() &&
      !!view &&
      this.statusFrameCurrent(view) &&
      context === this.publishedStatus()?.context &&
      this.statusSelection === entry?.selection &&
      entry?.view === view &&
      JSON.stringify(target) === JSON.stringify(entry?.target)
    );
  }

  private queuePublishedStatusFocus(
    context: string,
    task: WorkflowPublishedStatusSectionId,
    target: () => HTMLElement | undefined,
  ): void {
    const request = ++this.statusFocusRequest,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        request !== this.statusFocusRequest ||
        !this.statusContextCurrent(context) ||
        task !== this.statusTask() ||
        this.newerWorkFocus(active, before)
      )
        return;
      const heading = target();
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === heading &&
        request === this.statusFocusRequest &&
        this.statusContextCurrent(context) &&
        task === this.statusTask() &&
        heading.isConnected &&
        !heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  private resetPublishedStatus(): void {
    this.statusFocusRequest++;
    this.statusOpen.set(false);
    this.statusView.set(null);
    this.statusTarget.set(null);
    this.statusTask.set('businessStatus');
    this.statusSelection = null;
    this.statusOrigin = null;
  }
  requestEditWorkflow(entry = this.draftEditEntry()): void {
    if (
      !entry ||
      entry !== this.draftEditEntry() ||
      entry.source !== this.definition ||
      !this.draftEditNativeCurrent() ||
      !this.isDraftEditTargetCurrent(entry.request)
    )
      return;
    this.emittingDraftRequest = entry.request;
    try {
      this.editRequested.emit(entry.request);
    } finally {
      this.emittingDraftRequest = null;
    }
  }
  isCurrentDraftEditRequest(
    request: WorkflowPublishedDraftRequest,
    current = this.definition,
  ): boolean {
    return (
      !!request &&
      this.emittingDraftRequest === request &&
      this.draftEditNativeCurrent() &&
      request.ownerContext === this.availabilityOwnerContext &&
      request.view === this.activeView &&
      this.isDraftEditTargetCurrent(request, current)
    );
  }
  /** Pure target comparison for a trusted asynchronous handoff, independent of emission/portal state. */
  isDraftEditTargetCurrent(
    request: WorkflowPublishedDraftRequest,
    current = this.definition,
  ): boolean {
    return (
      !!request &&
      !!current &&
      current.definitionDocumentStatusCode === 'readable' &&
      request.definitionVersionId === current.id &&
      request.workflowCode === current.workflowCode &&
      request.definitionVersionCode === current.definitionVersionCode &&
      request.actionCode ===
        workflowPublishedDraftAction(current.draftActionCode) &&
      request.actionContext ===
        JSON.stringify([
          current.draftActionCode,
          Object.hasOwn(current, 'editableDraftId'),
          current.editableDraftId,
        ]) &&
      request.sourceContext ===
        readWorkflowPublishedWorkflowSettingsInspection(current).context
    );
  }
  private draftEditNativeCurrent(): boolean {
    const host = this.host.nativeElement,
      button = this.draftEditButton?.nativeElement;
    if (
      !this.canEdit ||
      this.loading ||
      this.interactionBlocked ||
      this.lifecycleInProgress() ||
      this.destroyRef.destroyed ||
      !host.isConnected ||
      !button?.isConnected ||
      button.matches(':disabled') ||
      button.closest('[hidden],[inert],[aria-hidden="true"]')
    )
      return false;
    for (
      let node: HTMLElement | null = button;
      node;
      node = node.parentElement
    ) {
      const style = host.ownerDocument.defaultView?.getComputedStyle(node);
      if (
        style?.display === 'none' ||
        style?.visibility === 'hidden' ||
        style?.visibility === 'collapse'
      )
        return false;
    }
    return true;
  }

  draftActionLabel(): string {
    return workflowDraftActionLabel(this.definition?.draftActionCode);
  }

  inspectReportedStatus(): void {
    this.reportedStatusOpen.set(true);
    setTimeout(() => this.reportedStatusSummary?.nativeElement.focus());
  }

  reviewAvailabilityControls(): void {
    const controls = this.lifecycleActionsElement?.nativeElement;
    const activate = controls?.querySelector<HTMLButtonElement>(
      '#workflow-make-current, #workflow-activate-now',
    );
    const target =
      activate && !activate.disabled
        ? activate
        : controls?.querySelector<HTMLElement>(
            '#workflow-current-availability-title',
          ) ?? controls?.querySelector<HTMLElement>(
            '#workflow-lifecycle-actions-title',
          );
    target?.focus();
  }
  setDiagnosticsOpen(open: boolean): void {
    this.diagnosticsOpen.set(open);
    if (open) this.diagnosticsLoaded.set(true);
  }

  readonly inspectorPane = signal<WorkflowVersionInspectorPane>('details');
  readonly mapSelection = signal<WorkflowReadOnlyGraphSelection | null>(null);
  readonly selectedDecisionGateId = signal<string | null>(null);
  readonly structuredInspectorResetVersion = signal(0);

  readonly definitionDocument = computed(
    () => this.definitionState()?.definitionDocument ?? null,
  );
  readonly definitionDocumentIssueCodes = computed(() =>
    normalizeIssueCodes(this.definitionState()?.definitionDocumentIssueCodes),
  );
  readonly graph = computed(() => {
    const document = this.definitionDocument();
    return document
      ? projectWorkflowDraftGraph(document, {
          sourceContext: 'published-version',
        })
      : null;
  });
  readonly selectedRendererElementId = computed(
    () => this.mapSelection()?.rendererId ?? null,
  );
  readonly selectedGraphElement =
    computed<WorkflowGraphInspectableElement | null>(() => {
      const graph = this.graph();
      const selection = this.mapSelection();
      if (!graph || !selection) {
        return null;
      }

      const candidates = selection.kind === 'node' ? graph.nodes : graph.edges;
      return (
        candidates.find(
          (candidate) =>
            candidate.id === selection.rendererId &&
            candidate.sourceId === selection.sourceId &&
            candidate.sourceIndex === selection.sourceIndex,
        ) ?? null
      );
    });
  readonly selectedGraphDetail =
    computed<WorkflowReadOnlyGraphElementDetail | null>(
      () => this.selectedGraphElement()?.detail ?? null,
    );
  readonly selectedNode = computed<WorkflowReadOnlyGraphNode | null>(() => {
    const selected = this.selectedGraphElement();
    return selected && 'decisionGates' in selected ? selected : null;
  });
  readonly selectedHumanStepNodeId = computed(() => {
    const selected = this.selectedNode();
    return selected?.kind === 'human-step' ? selected.sourceId : null;
  });
  readonly selectedDocumentNode = computed(() => {
    const document = this.definitionDocument();
    const selected = this.selectedNode();
    if (
      !document ||
      selected?.sourceIndex === null ||
      selected?.sourceIndex === undefined
    ) {
      return null;
    }
    return (document.nodes ?? [])[selected.sourceIndex] ?? null;
  });
  readonly selectedPathIndex = computed(() => {
    const selected = this.selectedGraphElement();
    return selected && !('decisionGates' in selected)
      ? selected.sourceIndex
      : null;
  });
  readonly mapContextSupportsBehavior = computed(() => {
    const selection = this.mapSelection();
    return !selection || this.selectedNode()?.kind === 'human-step';
  });
  readonly contextDecisionGates = computed<
    readonly WorkflowReadOnlyGraphDecisionGate[]
  >(() => {
    const graph = this.graph();
    if (!graph) {
      return [];
    }

    const selection = this.mapSelection();
    if (!selection) {
      return graph.workflowDecisionGates ?? [];
    }

    return selection.kind === 'node'
      ? (this.selectedNode()?.decisionGates ?? [])
      : [];
  });
  readonly selectedDecisionGate = computed(() => {
    const gateId = this.selectedDecisionGateId();
    return gateId
      ? (this.contextDecisionGates().find((gate) => gate.id === gateId) ?? null)
      : null;
  });
  readonly humanStepCount = computed(
    () =>
      this.graph()?.nodes.filter((node) => node.kind === 'human-step').length ??
      0,
  );
  readonly pathCount = computed(
    () => this.definitionDocument()?.edges?.length ?? 0,
  );
  readonly statusChangeCount = computed(
    () => this.graph()?.appliedTransitionCount ?? 0,
  );
  readonly decisionRuleCount = computed(
    () => this.graph()?.appliedDecisionCount ?? 0,
  );
  readonly contextStatusChangeCount = computed(() => {
    const selected = this.selectedDocumentNode();
    return selected?.typeCode === 'human_step'
      ? (selected.allowedTransitions?.length ?? 0)
      : (this.definitionDocument()?.workflowTransitions?.length ?? 0);
  });
  readonly contextDecisionRuleCount = computed(() => {
    const document = this.definitionDocument();
    const selected = this.selectedDocumentNode();
    if (selected?.typeCode === 'human_step') {
      return decisionScopeRuleCount(
        selected.requiredDecisions,
        selected.allowedTransitions,
      );
    }
    return document
      ? decisionScopeRuleCount(
          document.requiredDecisions,
          document.workflowTransitions,
        )
      : 0;
  });
  readonly savedReferenceCodes = computed(() =>
    collectReferenceCodes(this.definitionDocument()),
  );
  readonly hasDocumentWarnings = computed(() => {
    const graph = this.graph();
    return Boolean(
      graph && (graph.invalidElementCount > 0 || graph.malformedEntryCount > 0),
    );
  });

  selectView(view: WorkflowVersionWorkbenchView): void {
    if (view === this.activeView) {
      return;
    }

    this.resetPublishedStatus();
    this.closeHearingMore();
    this.technicalDocumentOpenContext.set(null);
    this.statusViewGeneration.update(generation => generation + 1);
    this.activeViewChange.emit(view);
  }

  requestBack(): void {
    this.backRequested.emit();
  }

  focusHeading(): void {
    this.workspaceHeading?.nativeElement.focus();
  }

  focusBackAction(): void {
    this.backButton?.nativeElement.focus();
  }

  focusActiveViewHeading(): void {
    const target =
      this.activeView === 'setup'
        ? this.setupHeading
        : this.activeView === 'review'
          ? this.reviewHeading
          : this.mapHeading;
    target?.nativeElement.focus();
  }

  onMapSelectionRequested(
    request: WorkflowReadOnlyGraphSelectionRequest,
  ): void {
    if (this.statusView() === 'map') this.resetPublishedStatus();
    if (this.workOpen()) this.resetPublishedWork();
    if (this.presentationOpen()) this.resetPublishedPresentation();
    this.resetPublishedRouting();
    this.mapSelection.set(request.selection);
    this.selectedDecisionGateId.set(null);
    this.inspectorPane.set(
      request.selection?.kind === 'edge' ? 'path' : 'details',
    );
    this.structuredInspectorResetVersion.update((version) => version + 1);
    if (request.moveFocusToInspector) {
      this.focusInspectorAfterRender();
    }
  }

  onDecisionGateRequested(
    request: WorkflowReadOnlyGraphDecisionGateRequest,
  ): void {
    const graph = this.graph();
    const gate = findDecisionGate(graph, request.gateId);
    if (!graph || !gate) {
      return;
    }

    this.resetPublishedRouting();
    if (this.statusView() === 'map') this.resetPublishedStatus();
    this.mapSelection.set(selectionForGateOwner(graph.nodes, gate));
    this.selectedDecisionGateId.set(gate.id);
    this.inspectorPane.set('decisions');
    this.structuredInspectorResetVersion.update((version) => version + 1);
    setTimeout(() => {
      this.decisionEditor?.focusDecisionGate(request);
      this.mapInspectorHeading?.nativeElement.focus();
    });
  }

  onDecisionScopeRequested(
    request: WorkflowReadOnlyGraphDecisionScopeRequest,
  ): void {
    const graph = this.graph();
    if (!graph) {
      return;
    }

    if (
      request.ownerSourceNodeId === null &&
      request.ownerSourceNodeIndex === null
    ) {
      this.resetPublishedRouting();
      if (this.statusView() === 'map') this.resetPublishedStatus();
      this.mapSelection.set(null);
    } else {
      const owner =
        graph.nodes.find(
          (node) =>
            node.sourceId === request.ownerSourceNodeId &&
            node.sourceIndex === request.ownerSourceNodeIndex,
        ) ?? null;
      if (!owner) {
        return;
      }
      this.resetPublishedRouting();
      if (this.statusView() === 'map') this.resetPublishedStatus();
      this.mapSelection.set(toSelection(owner));
    }

    this.selectedDecisionGateId.set(null);
    this.inspectorPane.set('decisions');
    this.structuredInspectorResetVersion.update((version) => version + 1);
    setTimeout(() => {
      this.decisionEditor?.focusDecisionOverview(request.ownerSourceNodeId);
      this.mapInspectorHeading?.nativeElement.focus();
    });
  }

  selectInspectorPane(pane: WorkflowVersionInspectorPane): void {
    if (this.statusOpen() && this.statusView() === 'map') return;
    if (
      (pane === 'status-changes' || pane === 'decisions') &&
      !this.mapContextSupportsBehavior()
    ) {
      return;
    }
    if (pane !== 'decisions') {
      this.selectedDecisionGateId.set(null);
    }
    this.inspectorPane.set(pane);
    this.structuredInspectorResetVersion.update((version) => version + 1);
    if (pane === 'decisions') {
      setTimeout(() =>
        this.decisionEditor?.focusDecisionOverview(
          this.selectedHumanStepNodeId(),
        ),
      );
    }
  }

  onTransitionDecisionRulesRequested(
    request: WorkflowDraftTransitionDecisionRulesRequest,
  ): void {
    if (this.statusOpen() && this.statusView() === 'map') return;
    this.inspectorPane.set('decisions');
    this.structuredInspectorResetVersion.update((version) => version + 1);
    setTimeout(() => this.decisionEditor?.focusTransitionDecisions(request));
  }

  showWholeWorkflow(): void {
    if (this.statusView() === 'map') this.resetPublishedStatus();
    this.resetPublishedRouting();
    this.mapSelection.set(null);
    this.selectedDecisionGateId.set(null);
    this.inspectorPane.set('details');
    this.structuredInspectorResetVersion.update((version) => version + 1);
    this.focusInspectorAfterRender();
  }

  mapInspectorContextLabel(): string {
    if (this.selectedDecisionGate()) {
      return 'Decision requirement';
    }
    const selected = this.selectedGraphElement();
    return selected?.detail.subtitle ?? 'Immutable workflow version';
  }

  mapInspectorTitle(): string {
    return (
      this.selectedDecisionGate()?.title ??
      this.selectedGraphDetail()?.title ??
      'Whole workflow'
    );
  }

  definitionDocumentStatusLabel(): string {
    if (this.definitionDocument()) {
      return 'Saved document available';
    }

    const status = normalizedDocumentStatus(this.definitionState());
    if (isIntegrityMismatchDocumentStatus(status)) {
      return 'Saved document failed integrity verification';
    }
    if (isLegacyDocumentStatus(status)) {
      return 'Authored document not stored';
    }
    if (isUnreadableDocumentStatus(status)) {
      return 'Saved document cannot be read';
    }
    return 'Authored document unavailable';
  }

  definitionDocumentFallbackHeading(): string {
    const status = normalizedDocumentStatus(this.definitionState());
    return isIntegrityMismatchDocumentStatus(status)
      ? 'This saved workflow document failed integrity verification'
      : isUnreadableDocumentStatus(status)
        ? 'This saved workflow document cannot be displayed'
        : isLegacyDocumentStatus(status)
          ? 'This version has no saved authored workflow document'
          : 'The authored workflow document is unavailable';
  }

  definitionDocumentFallbackMessage(): string {
    const status = normalizedDocumentStatus(this.definitionState());
    return isIntegrityMismatchDocumentStatus(status)
      ? 'The saved document identity or canonical content does not match the recorded version header. The map is withheld so unverified authored content is never presented as the selected version.'
      : isUnreadableDocumentStatus(status)
        ? 'The application cannot safely read this document version. The map is withheld rather than showing a partial or reconstructed workflow.'
        : isLegacyDocumentStatus(status)
          ? 'This registry record has no saved authored workflow document. Current runtime diagnostics can report flattened steps, status changes, and requirements for matching codes, but those values are not version-stored and cannot reconstruct nodes, paths, display order, or notifications.'
          : 'No canonical authored document was returned for this version. Current runtime diagnostics remain visible below, but they are not version-stored authored content and are not treated as an editor document.';
  }

  definitionDocumentTechnicalStatus(): string {
    return normalizedDocumentStatus(this.definitionState()) || 'not_reported';
  }

  documentScopeLabel(): string {
    const document = this.definitionDocument();
    if (!document) {
      return 'Not available';
    }
    return document.scopeKey
      ? `${humanizeCode(document.scopeTypeCode)} · ${document.scopeKey}`
      : humanizeCode(document.scopeTypeCode);
  }

  configurationLabel(): string {
    const document = this.definitionDocument();
    const definition = this.definitionState();
    const code = document?.configurationCode ?? definition?.configurationCode;
    const version =
      document?.configurationVersionCode ??
      definition?.configurationVersionCode;
    return [code, version].filter(hasValue).join(' / ') || 'Not available';
  }

  formatCode(value: string | null | undefined): string {
    return humanizeCode(value);
  }

  formatEffectiveWindow(): string {
    const definition = this.definitionState();
    if (!definition) {
      return 'Not available';
    }
    const start = formatDateTime(definition.effectiveStartUtc);
    const end = definition.effectiveEndUtc?.trim();
    return end ? `${start} to ${formatDateTime(end)}` : `${start} onward`;
  }

  private resetMapContext(): void {
    this.resetPublishedWork();
    this.resetPublishedPresentation();
    this.resetPublishedRouting();
    this.resetPublishedStatus();
    this.inspectorPane.set('details');
    this.mapSelection.set(null);
    this.selectedDecisionGateId.set(null);
    this.structuredInspectorResetVersion.update((version) => version + 1);
  }

  private focusInspectorAfterRender(): void {
    const workRequest = this.workFocusRequest,
      presentationRequest = this.presentationFocusRequest,
      routingRequest = this.routingFocusRequest,
      statusRequest = this.statusFocusRequest;
    setTimeout(() => {
      if (
        !this.workOpen() &&
        !this.presentationOpen() &&
        !this.routingOpen() &&
        !(this.statusOpen() && this.statusView() === 'map') &&
        statusRequest === this.statusFocusRequest &&
        routingRequest === this.routingFocusRequest &&
        workRequest === this.workFocusRequest &&
        presentationRequest === this.presentationFocusRequest
      )
        this.mapInspectorHeading?.nativeElement.focus();
    });
  }

}

function findDecisionGate(
  graph: ReturnType<typeof projectWorkflowDraftGraph> | null,
  gateId: string,
): WorkflowReadOnlyGraphDecisionGate | null {
  return (
    graph?.workflowDecisionGates?.find((gate) => gate.id === gateId) ??
    graph?.nodes
      .flatMap((node) => node.decisionGates ?? [])
      .find((gate) => gate.id === gateId) ??
    null
  );
}

function selectionForGateOwner(
  nodes: readonly WorkflowReadOnlyGraphNode[],
  gate: WorkflowReadOnlyGraphDecisionGate,
): WorkflowReadOnlyGraphSelection | null {
  if (gate.ownerSourceIndex === null) {
    return null;
  }
  const owner =
    nodes.find(
      (node) =>
        node.sourceId === gate.ownerSourceId &&
        node.sourceIndex === gate.ownerSourceIndex,
    ) ?? null;
  return owner ? toSelection(owner) : null;
}

function toSelection(
  element: WorkflowReadOnlyGraphNode | WorkflowReadOnlyGraphEdge,
): WorkflowReadOnlyGraphSelection {
  return {
    kind: 'decisionGates' in element ? 'node' : 'edge',
    rendererId: element.id,
    sourceId: element.sourceId,
    sourceIndex: element.sourceIndex,
    isValid: !element.isInvalid,
    isAuthorable: false,
  };
}

function normalizeIssueCodes(
  value: readonly string[] | null | undefined,
): string[] {
  return [
    ...new Set((value ?? []).map((code) => code.trim()).filter(hasValue)),
  ];
}

function normalizedDocumentStatus(
  definition: WorkflowDefinitionVersionDetail | null,
): string {
  return (
    definition?.definitionDocumentStatusCode?.trim().toLocaleLowerCase() ?? ''
  );
}

function isLegacyDocumentStatus(status: string): boolean {
  return (
    status.includes('legacy') ||
    status.includes('not_stored') ||
    status.includes('not_available_for_version')
  );
}

function isIntegrityMismatchDocumentStatus(status: string): boolean {
  return (
    status.includes('integrity_mismatch') ||
    status.includes('integrity mismatch')
  );
}

function isUnreadableDocumentStatus(status: string): boolean {
  return (
    status.includes('unreadable') ||
    status.includes('unsupported') ||
    status.includes('invalid')
  );
}

function humanizeCode(value: string | null | undefined): string {
  const normalized = value?.trim();
  if (!normalized) {
    return 'Not recorded';
  }
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toLocaleUpperCase());
}

function formatDateTime(value: string | null | undefined): string {
  const normalized = value?.trim();
  if (!normalized) {
    return 'Not recorded';
  }
  const date = new Date(normalized);
  return Number.isNaN(date.getTime())
    ? normalized
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

function hasValue(value: string | null | undefined): value is string {
  return Boolean(value?.trim());
}

function referenceRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function referenceRows(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function decisionScopeRuleCount(
  directDecisions: unknown,
  transitions: unknown,
): number {
  return (
    decisionRequirementCount(directDecisions) +
    referenceRows(transitions).reduce<number>(
      (count, transition) =>
        count +
        (referenceRecord(transition)
          ? decisionRequirementCount(
              transition['requiredDecisions'],
              transition['requiredDecisionKinds'],
            )
          : 0),
      0,
    )
  );
}

function decisionRequirementCount(
  decisions: unknown,
  legacyDecisionKinds: unknown = null,
): number {
  const requirementKeys = new Set<string>();
  referenceRows(decisions).forEach((decision, index) => {
    requirementKeys.add(
      referenceRecord(decision)
        ? decisionRequirementKey(decision)
        : `incomplete-structured:${index}`,
    );
  });
  referenceRows(legacyDecisionKinds).forEach((decisionKind, index) => {
    const normalizedKind =
      typeof decisionKind === 'string' ? decisionKind.trim() : '';
    requirementKeys.add(
      normalizedKind
        ? `${normalizedKind.toLocaleLowerCase()}|approved|permit|`
        : `incomplete-legacy:${index}`,
    );
  });
  return requirementKeys.size;
}

function decisionRequirementKey(decision: Record<string, unknown>): string {
  const rawReaction = decision['reaction'];
  const reaction = referenceRecord(rawReaction) ? rawReaction : null;
  // Preserve valid legacy nullish count keys; malformed scalars never become codes.
  return [
    decision['decisionKind'],
    decision['requiredOutcomeCode'] ?? 'approved',
    reaction
      ? (reaction['reactionCode'] ?? 'permit')
      : rawReaction == null
        ? 'permit'
        : '',
    reaction?.['targetStateCode'] ?? '',
  ]
    .map((value) =>
      typeof value === 'string' ? value.trim().toLocaleLowerCase() : '',
    )
    .join('|');
}

function collectReferenceCodes(
  document: WorkflowDefinitionDocument | null,
): WorkflowVersionReferenceCodes {
  const values = {
    stateCodes: new Set<string>(),
    decisionKindCodes: new Set<string>(),
    decisionOutcomeCodes: new Set<string>(),
    decisionReactionCodes: new Set<string>(),
    roleCodes: new Set<string>(),
    queueCodes: new Set<string>(),
    managerNotificationTargetCodes: new Set<string>(),
    externalNotificationTargetCodes: new Set<string>(),
  };

  const addNotification = (notification: unknown): void => {
    if (!referenceRecord(notification)) return;
    const target =
      typeof notification['recipientTargetKey'] === 'string'
        ? notification['recipientTargetKey'].trim()
        : null;
    const recipientTypeCode =
      typeof notification['recipientTypeCode'] === 'string'
        ? notification['recipientTypeCode'].trim().toLocaleLowerCase()
        : '';
    switch (recipientTypeCode) {
      case 'queue':
        addCode(values.queueCodes, target);
        break;
      case 'role':
        addCode(values.roleCodes, target);
        break;
      case 'manager':
        addCode(values.managerNotificationTargetCodes, target);
        break;
      case 'external_destination':
        addCode(values.externalNotificationTargetCodes, target);
        break;
      default:
        break;
    }
  };

  const addDecision = (decision: unknown): void => {
    if (!referenceRecord(decision)) return;
    const reaction = referenceRecord(decision['reaction'])
      ? decision['reaction']
      : null;
    addCode(values.decisionKindCodes, decision['decisionKind']);
    addCode(values.decisionOutcomeCodes, decision['requiredOutcomeCode']);
    addCode(values.decisionReactionCodes, reaction?.['reactionCode']);
    addCode(values.stateCodes, reaction?.['targetStateCode']);
    addCode(values.queueCodes, decision['ownerQueueCode']);
    referenceRows(decision['eligibleRoleCodes']).forEach((roleCode) =>
      addCode(values.roleCodes, roleCode),
    );
    referenceRows(decision['notifications']).forEach(addNotification);
  };

  const addTransition = (transition: unknown): void => {
    if (!referenceRecord(transition)) return;
    addCode(values.stateCodes, transition['fromStateCode']);
    addCode(values.stateCodes, transition['toStateCode']);
    referenceRows(transition['requiredDecisionKinds']).forEach((kind) =>
      addCode(values.decisionKindCodes, kind),
    );
    referenceRows(transition['requiredDecisions']).forEach(addDecision);
    referenceRows(transition['notifications']).forEach(addNotification);
  };

  if (referenceRecord(document)) {
    referenceRows(document['assignmentPolicies']).forEach((policy) => {
      if (!referenceRecord(policy)) return;
      addCode(values.queueCodes, policy['initialQueueCode']);
      referenceRows(policy['eligibleQueueCodes']).forEach((code) =>
        addCode(values.queueCodes, code),
      );
      referenceRows(policy['eligibleRoleCodes']).forEach((code) =>
        addCode(values.roleCodes, code),
      );
    });
    referenceRows(document['requiredDecisions']).forEach(addDecision);
    referenceRows(document['workflowTransitions']).forEach(addTransition);
    referenceRows(document['nodes']).forEach((node) => {
      if (!referenceRecord(node)) return;
      referenceRows(node['requiredDecisions']).forEach(addDecision);
      referenceRows(node['allowedTransitions']).forEach(addTransition);
    });
  }

  return {
    stateCodes: sortedCodes(values.stateCodes),
    decisionKindCodes: sortedCodes(values.decisionKindCodes),
    decisionOutcomeCodes: sortedCodes(values.decisionOutcomeCodes),
    decisionReactionCodes: sortedCodes(values.decisionReactionCodes),
    roleCodes: sortedCodes(values.roleCodes),
    queueCodes: sortedCodes(values.queueCodes),
    managerNotificationTargetCodes: sortedCodes(
      values.managerNotificationTargetCodes,
    ),
    externalNotificationTargetCodes: sortedCodes(
      values.externalNotificationTargetCodes,
    ),
  };
}

function addCode(target: Set<string>, value: unknown): void {
  const normalized = typeof value === 'string' ? value.trim() : '';
  if (normalized) {
    target.add(normalized);
  }
}

function sortedCodes(values: ReadonlySet<string>): string[] {
  return [...values].sort((left, right) => left.localeCompare(right));
}
